/**
 * Content Manager - Constants
 *
 * Centralised constants for colours, defaults, and configuration.
 */

import type {
  ContentStatus,
  PriorityLevel,
  ContentType,
  AppConfig,
  LoggingConfig,
} from '../types';

// =============================================================================
// BRAND COLOURS
// =============================================================================

export const BRAND_COLOURS = {
  navy: '#253746',
  orange: '#ed8b00',
  white: '#ffffff',
  lightGrey: '#f8f9fa',
  mediumGrey: '#e0e0e0',
  darkGrey: '#374151',
} as const;

// =============================================================================
// STATUS COLOURS
// =============================================================================

export const STATUS_COLOURS: Record<ContentStatus, string> = {
  'Idea': '#6b7280',
  'Scheduled': '#3b82f6',
  'In Progress': '#f59e0b',
  'Review': '#8b5cf6',
  'Approved': '#10b981',
  'Published': '#059669',
  'Archived': '#6b7280',
} as const;

export const STATUS_BACKGROUND_COLOURS: Record<ContentStatus, string> = {
  'Idea': '#f3f4f6',
  'Scheduled': '#eff6ff',
  'In Progress': '#fffbeb',
  'Review': '#f5f3ff',
  'Approved': '#ecfdf5',
  'Published': '#ecfdf5',
  'Archived': '#f3f4f6',
} as const;

// =============================================================================
// PRIORITY COLOURS
// =============================================================================

export const PRIORITY_COLOURS: Record<PriorityLevel, string> = {
  'Low': '#6b7280',
  'Medium': '#3b82f6',
  'High': '#f97316',
  'Urgent': '#dc2626',
} as const;

// =============================================================================
// STATUS OPTIONS (For dropdowns)
// =============================================================================

export const STATUS_OPTIONS: ContentStatus[] = [
  'Idea',
  'Scheduled',
  'In Progress',
  'Review',
  'Approved',
  'Published',
  'Archived',
];

export const PRIORITY_OPTIONS: PriorityLevel[] = [
  'Low',
  'Medium',
  'High',
  'Urgent',
];

// =============================================================================
// DEFAULT CONTENT TYPES
// =============================================================================

export const DEFAULT_CONTENT_TYPES: ContentType[] = [
  'Article',
  'Social Post',
  'Video',
  'Infographic',
  'Email',
  'Case Study',
  'White Paper',
  'Webinar',
  'Podcast',
  'Newsletter',
];

// =============================================================================
// DEFAULT PLATFORMS
// =============================================================================

export const DEFAULT_PLATFORMS: string[] = [
  'LinkedIn',
  'Twitter',
  'Facebook',
  'Instagram',
  'YouTube',
  'Website',
  'Email',
  'TikTok',
  'Medium',
  'Substack',
];

// =============================================================================
// DEFAULT PLATFORM BRANDS
// =============================================================================

export const DEFAULT_PLATFORM_BRANDS: Record<string, { name: string; colour: string; icon?: string }> = {
  'LinkedIn': { name: 'LinkedIn', colour: '#0A66C2' },
  'Twitter': { name: 'Twitter', colour: '#1DA1F2' },
  'Facebook': { name: 'Facebook', colour: '#1877F2' },
  'Instagram': { name: 'Instagram', colour: '#E4405F' },
  'YouTube': { name: 'YouTube', colour: '#FF0000' },
  'Website': { name: 'Website', colour: '#6b7280' },
  'Email': { name: 'Email', colour: '#ea4335' },
  'TikTok': { name: 'TikTok', colour: '#000000' },
  'Medium': { name: 'Medium', colour: '#000000' },
  'Substack': { name: 'Substack', colour: '#FF6719' },
};

// =============================================================================
// DEFAULT CONTENT SILOS
// =============================================================================

export const DEFAULT_CONTENT_SILOS: string[] = [
  'Product Marketing',
  'Thought Leadership',
  'Customer Success',
  'Brand Awareness',
  'Lead Generation',
  'Employee Advocacy',
  'Partnerships',
  'Events',
];

// =============================================================================
// DEFAULT LOGGING CONFIGURATION
// =============================================================================

export const DEFAULT_LOGGING_CONFIG: LoggingConfig = {
  enabled: false,
  level: 'INFO',
  persistLogs: true,
  maxLogEntries: 500,
  showTimestamps: true,
  categories: {
    api: true,
    auth: true,
    content: true,
    config: true,
    realtime: true,
  },
};

export const LOG_LEVEL_OPTIONS = [
  { value: 'DEBUG', label: 'Debug', description: 'All messages including detailed debugging info' },
  { value: 'INFO', label: 'Info', description: 'General information and above' },
  { value: 'WARN', label: 'Warning', description: 'Warnings and errors only' },
  { value: 'ERROR', label: 'Error', description: 'Errors only' },
] as const;

// =============================================================================
// DEFAULT APP CONFIGURATION
// =============================================================================

export const DEFAULT_CREDENTIALS = {
  ahrefsApiKey: null,
  claudeApiKey: null,
  gaPropertyId: null,
  gscSiteUrl: null,
};

export const DEFAULT_APP_CONFIG: AppConfig = {
  id: 1,
  accentColour: '#ed8b00',
  theme: 'light',
  hidePerformanceTab: false,
  contentTypes: DEFAULT_CONTENT_TYPES,
  platforms: DEFAULT_PLATFORMS,
  contentSilos: DEFAULT_CONTENT_SILOS,
  platformBrands: DEFAULT_PLATFORM_BRANDS,
  csvFieldMapping: {
    title: 'Post Title',
    content: 'Post Content',
    publishDate: 'Scheduled Date',
    platforms: 'Networks',
    socialCopy: 'Message',
    hashtags: 'Hashtags',
    featuredImage: 'Media URL',
  },
  logging: DEFAULT_LOGGING_CONFIG,
  credentials: DEFAULT_CREDENTIALS,
  updatedAt: new Date().toISOString(),
};

// =============================================================================
// USER ROLES
// =============================================================================

export const USER_ROLE_OPTIONS = [
  { value: 'Admin', label: 'Admin', description: 'Full access to all features and user management' },
  { value: 'Editor', label: 'Editor', description: 'Can create, edit, and publish all content' },
] as const;

// =============================================================================
// KEYBOARD SHORTCUTS
// =============================================================================

export const KEYBOARD_SHORTCUTS = {
  NEW_CONTENT: { key: 'n', description: 'Create new content' },
  HELP: { key: '?', description: 'Show help/shortcuts' },
  COMMAND_PALETTE: { key: 'k', modifiers: ['meta'], description: 'Open command palette' },
  SEARCH: { key: '/', description: 'Focus search' },
  ESCAPE: { key: 'Escape', description: 'Close modal/cancel' },
  SAVE: { key: 's', modifiers: ['meta'], description: 'Save changes' },
  DELETE: { key: 'Delete', description: 'Delete selected' },
  NAV_UP: { key: 'ArrowUp', description: 'Navigate up' },
  NAV_DOWN: { key: 'ArrowDown', description: 'Navigate down' },
  NAV_LEFT: { key: 'ArrowLeft', description: 'Collapse/navigate left' },
  NAV_RIGHT: { key: 'ArrowRight', description: 'Expand/navigate right' },
  SELECT_ALL: { key: 'a', modifiers: ['meta'], description: 'Select all' },
} as const;

// =============================================================================
// VIEW MODE OPTIONS
// =============================================================================

export const VIEW_MODE_OPTIONS = [
  { value: 'content', label: 'Content', icon: 'FileText' },
  { value: 'calendar', label: 'Calendar', icon: 'Calendar' },
  { value: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
  { value: 'silos', label: 'Categories', icon: 'FolderTree' },
  { value: 'users', label: 'Team', icon: 'Users' },
  { value: 'audit', label: 'Audit Trail', icon: 'History' },
  { value: 'performance', label: 'Analytics', icon: 'BarChart3' },
] as const;

// =============================================================================
// LAYOUT CONSTANTS
// =============================================================================

export const LAYOUT = {
  DRAWER_WIDTH: 280,
  COLLAPSED_DRAWER_WIDTH: 72,
  APPBAR_HEIGHT: 64,
  SIDEBAR_BG: '#f8f9fa',
  SIDEBAR_BORDER: '#e0e0e0',
  CONTENT_BG: '#fafafa',
} as const;

// =============================================================================
// DATE FORMAT OPTIONS
// =============================================================================

export const DATE_FORMATS = {
  DISPLAY: 'dd/MM/yyyy',
  DISPLAY_SHORT: 'dd/MM/yy',
  DISPLAY_LONG: 'EEEE, dd/MM/yyyy',
  INPUT: 'yyyy-MM-dd',
  DATETIME: 'dd/MM/yyyy HH:mm',
  TIME: 'HH:mm',
} as const;

// =============================================================================
// TOAST DURATIONS
// =============================================================================

export const TOAST_DURATIONS = {
  SUCCESS: 3000,
  ERROR: 5000,
  WARNING: 4000,
  INFO: 3000,
} as const;

// =============================================================================
// PAGINATION DEFAULTS
// =============================================================================

export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 50,
  PAGE_SIZE_OPTIONS: [25, 50, 100, 200],
} as const;

// =============================================================================
// VALIDATION LIMITS
// =============================================================================

export const VALIDATION_LIMITS = {
  TITLE_MAX_LENGTH: 200,
  DESCRIPTION_MAX_LENGTH: 2000,
  EXCERPT_MAX_LENGTH: 500,
  SEO_TITLE_MAX_LENGTH: 200,
  META_DESCRIPTION_MAX_LENGTH: 300,
  NOTE_MAX_LENGTH: 5000,
  TAG_MAX_LENGTH: 50,
  MAX_TAGS: 20,
  MAX_PLATFORMS: 10,
  MAX_ASSETS: 20,
} as const;

// =============================================================================
// EXPORT FIELD OPTIONS (For CSV export)
// =============================================================================

export const EXPORT_FIELD_OPTIONS = [
  { value: 'title', label: 'Title' },
  { value: 'content', label: 'Content' },
  { value: 'excerpt', label: 'Excerpt' },
  { value: 'socialCopy', label: 'Social Copy' },
  { value: 'publishDate', label: 'Publish Date' },
  { value: 'platforms', label: 'Platforms' },
  { value: 'hashtags', label: 'Hashtags' },
  { value: 'featuredImage', label: 'Featured Image' },
  { value: 'seoTitle', label: 'SEO Title' },
  { value: 'metaDescription', label: 'Meta Description' },
  { value: 'focusKeyword', label: 'Focus Keyword' },
  { value: 'status', label: 'Status' },
  { value: 'author', label: 'Author' },
  { value: 'contentType', label: 'Content Type' },
] as const;
